import { ID_PART_PATTERN } from "./id-grammar.js";
import { RESOURCE_SLUG_PATTERN } from "./slug-grammar.js";
import { safeRuleLabel } from "./fixture-placement-rules.js";
//#region src/fixture-schema-derivation.ts
/**
* Postgres identifier limit. Postgres truncates silently past `NAMEDATALEN - 1`, and a silently
* truncated schema name is a collision waiting to happen, so the derivation refuses instead.
*/
const PG_NAMEDATALEN = 63;
/**
* The schema-name grammar the platform accepts. Identical to the pattern
* `fixture.schema.json` applied to the retired `ownSchema` field and to
* `SCHEMA_NAME_PATTERN` in the operator's `db-credential-reconciler`, so a derived name is legal
* everywhere a declared one was.
*/
const SCHEMA_NAME_PATTERN = /^[a-z][a-z0-9_]*$/;
/**
* Schemas the platform owns and no fixture may own or read.
*
* ⛔ This is the EXACT set of schemas DP core-api declares with `pgSchema(...)`
* (`cloud/dp/core-api/src`). It is a lockstep list: if core-api gains an owned schema, it is added
* here in the same change. It was stale by one before this feature — core-api declared `work` and
* the operator's copy held only `auth` and `release` — which left `work` grantable through
* `readSchemas`.
*
* Moved into `@zion/sdk-contracts` from the operator so the publish door and the operator apply
* one list rather than two copies that can drift.
*/
const RESERVED_SCHEMAS = /* @__PURE__ */ new Set([
	"auth",
	"release",
	"work"
]);
/**
* Derive the Postgres schema a fixture owns, from the app slug and the fixture id.
*
* `<appSlug>_<fixtureId>`, with every hyphen replaced by an underscore. App slugs admit `-` and
* the schema grammar does not, so that one substitution is the whole transformation.
*
* ⛔ NOTHING ELSE IS TRANSFORMED. No case folding, no truncation, no stripping of other
* characters. An input that does not yield a legal name is REFUSED, never repaired. A repair
* would map two distinct inputs onto one schema, which is the collision this exists to reduce.
*
* ⚠️ THIS FUNCTION IS NOT INJECTIVE ACROSS APPS, and the docblock says so rather than claiming a
* guarantee it does not give. `wai-infra`+`policy-admin`, `wai`+`infra-policy-admin` and
* `wai-infra-policy`+`admin` all derive `wai_infra_policy_admin`; the join is ambiguous with a
* hyphen inside either component. WITHIN one app it IS injective: the slug is fixed, fixture ids
* are unique, and neither component may contain `_`. Across apps the control is the operator's
* `AUTHORIZATION` ownership refusal, not this function. A separator change does not fix it,
* because `RESOURCE_SLUG_PATTERN` permits consecutive hyphens by design.
*
* Length is asserted rather than assumed: the constants live in other files and can move.
*
* @param appSlug   the app's REGISTERED slug, never `app.json`'s `appId`. Where they have drifted
*                  the registry is authoritative, matching `validateAppApiPathNamespace`.
* @param fixtureId the fixture id as declared in `fixture.json`.
*/
function deriveFixtureSchemaName(appSlug, fixtureId) {
	if (!RESOURCE_SLUG_PATTERN.test(appSlug)) return {
		ok: false,
		failure: {
			kind: "app-slug-illegal",
			appSlug
		}
	};
	if (!ID_PART_PATTERN.test(fixtureId)) return {
		ok: false,
		failure: {
			kind: "fixture-id-illegal",
			fixtureId
		}
	};
	const derived = `${appSlug}_${fixtureId}`.replaceAll("-", "_");
	if (!SCHEMA_NAME_PATTERN.test(derived)) return {
		ok: false,
		failure: {
			kind: "app-slug-illegal",
			appSlug
		}
	};
	if (derived.length > 63) return {
		ok: false,
		failure: {
			kind: "too-long",
			derived,
			length: derived.length
		}
	};
	return {
		ok: true,
		schema: derived
	};
}
/**
* Whether the manifest DECLARED a schema name at all, independent of whether that name is legal.
*
* ⛔ THE ONE AUTHORED HOME FOR THE DECLARED-ARM TEST, PER C-69. `resolveFixtureSchemaName`'s
* declared arm opens with exactly this predicate, and a caller that needs to branch on *was
* anything declared* must call this rather than restating it. A fourth inline copy of
* `typeof x === 'string' && x !== ''` was found in the publish-door rule while every copy still
* matched, which is the condition under which it is still a finding.
*
* ⛔ IT TAKES THE RAW VALUE, NOT A RESULT, AND THAT IS THE WHOLE POINT. The obvious alternative —
* `resolveFixtureSchemaName(...).origin === 'declared'` — CANNOT WORK: `origin` exists only on the
* success result, so a declared-but-illegal value yields no origin and the caller cannot tell
* *declared and broken* from *nothing declared*. Those two need opposite handling.
*
* ⭐ The general shape: a derived-value accessor cannot answer a question about its INPUT, because
* a refusal destroys the accessor's subject.
*
* ⛔ THE RETURN TYPE IS A TYPE PREDICATE, NOT `boolean`, AND THAT IS FORCED RATHER THAN CHOSEN.
* `resolveFixtureSchemaName`'s declared arm reads `declared.length` and hands `declared` to
* `SCHEMA_NAME_PATTERN.test`, and both need a `string`. Returning plain `boolean` narrows nothing,
* so that arm does not compile: measured as 8 `TS2345`/`TS2322`/`TS18046` errors across the six
* lines of the arm. The only alternatives were restating the condition inline, which C-69 exists to
* forbid, and an `as string` cast, which hides the same widening behind a silent assertion.
* ⭐ It also gives a caller narrowing for free: `if (hasDeclaredSchema(db.ownSchema))` narrows the
* property, so nothing downstream needs a second `typeof` test either.
*/
function hasDeclaredSchema(declared) {
	return typeof declared === "string" && declared !== "";
}
/**
* C-4's EXPAND-PHASE PRECEDENCE, written once, as code, for every caller (C-39).
*
* ⛔⛔ READ THIS BEFORE CHANGING THE ORDER. A DECLARED value WINS. If the derived name won instead,
* every fixture in the fleet would be handed a fresh schema at its next reconcile: `perf` would
* become `perf_control`, the operator would create an empty `perf_control`, and the live data would
* sit in `perf` with nothing pointing at it. The ownership refusal does not save it, because the
* new name is unclaimed. The ordering IS the prevention.
*
* ⛔ ONE IMPLEMENTATION FOR THREE CONSUMERS: release-engine's effective-name comparison (C-15), the
* operator's single named local (C-18), and `@zion/sdk-node`'s R2 (C-11, C-16). Three inline copies
* of one precedence rule is the drift this feature exists to remove, reproduced inside it. C-18's
* shape is preserved exactly: the operator's local is simply initialised from this call.
*
* ⛔ A DECLARED VALUE IS STILL VALIDATED: grammar, then length, then `RESERVED_SCHEMAS`.
* ⚠️ **THIS IS WHERE THE RESERVED CHECK LIVES, AND C-14 IS WHY.** It was deleted from the
* derivation because a two-part derived name always contains `_` and no reserved member does, so
* that arm could never execute and a dead branch reds this package's `branches: 100`. On a DECLARED
* value `auth` is genuinely reachable. Nothing was lost; the check moved to where it can fire.
* ⚠️ It is NEW for `ownSchema`: the operator's denylist guarded `readSchemas` and not `ownSchema`,
* so `ownSchema: 'auth'` was stopped only by the second-order accident that `auth` already exists
* under another owner. Measured on `dp-app`: no live app schema is `auth`, `release` or `work`.
*
* ⚠️ ABSENT AND EMPTY ARE THE SAME THING HERE, and the `''` arm is not defensive padding. The JSON
* schema gives `ownSchema` no `minLength`, so `{"ownSchema": ""}` has been publishable for as long
* as the field has existed. Treating it as declared carries an empty string into `CREATE SCHEMA ""`.
*
* @param declared  the manifest's `ownSchema`, as `unknown`: callers run before or without schema
*                  validation, the contract every rule in this package offers.
* @param appSlug   the REGISTERED slug at an enforcing caller. ⚠️ At the operator this is `appKey`,
*                  which is the registry's slug by circumstance rather than by construction: three
*                  producers feed that key and only one reads `apps.slug` (C-34).
* @param fixtureId the fixture id.
*/
function resolveFixtureSchemaName(declared, appSlug, fixtureId) {
	if (hasDeclaredSchema(declared)) {
		if (!SCHEMA_NAME_PATTERN.test(declared)) return {
			ok: false,
			failure: {
				kind: "declared-illegal",
				declared
			}
		};
		if (declared.length > 63) return {
			ok: false,
			failure: {
				kind: "declared-too-long",
				declared,
				length: declared.length
			}
		};
		if (RESERVED_SCHEMAS.has(declared)) return {
			ok: false,
			failure: {
				kind: "declared-reserved",
				declared
			}
		};
		return {
			ok: true,
			schema: declared,
			origin: "declared"
		};
	}
	const derived = deriveFixtureSchemaName(appSlug, fixtureId);
	if (!derived.ok) return derived;
	return {
		ok: true,
		schema: derived.schema,
		origin: "derived"
	};
}
/**
* Render any schema-name failure as the message a publishing author reads. One wording, every door.
*
* ⛔ THIS IS THE ONLY EXPORTED RENDERER, PER C-57. There were two — `schemaDerivationMessage` over
* the narrow union and `schemaResolutionMessage` over the wide one — and **two near-identically
* named renderers over two overlapping unions produced FOUR defects in one plan set, by two
* different authors**, every one a call to the wrong member of the pair. `schemaDerivationMessage`
* is DELETED rather than made private: a private helper is one export statement away from
* re-arming the confusion, and the next author will not know why it was private.
*
* ⛔ EVERY INTERPOLATED VALUE GOES THROUGH `safeRuleLabel`, PER B-5. `fixtureId`, `appSlug`,
* `declared` and `entry` are all author-controlled and NONE is length-bounded: `fixture.schema.json`
* gives `fixtureId` no `maxLength`, and the wire schema for `ownSchema` is `z.string().min(1)` with
* no max and no pattern. These rules run **even when schema validation failed**, so a hostile value
* reaches this renderer by design rather than by accident. `safeRuleLabel` caps at 64 characters and
* replaces every non-printable-ASCII byte with `?`, which neutralises both the flood and the log
* forge. The house convention is unanimous: the sibling rule files score 10, 9, 13, 2, 9 and 15,
* against a control of 0 in a non-rule file in the same directory.
*
* ⛔ WHERE `safeRuleLabel` COMES FROM, because it is not in this file and not in the barrel:
* `contracts/src/fixture-placement-rules.ts` exports it, same package, sibling file. WS-4 §5.1's
* import list gains it. It is `(v: unknown) => string`, so it is safe on a value the schema has not
* validated, which is the case these rules are bound for.
*
* ⚠️ C-57 WIDENED THIS BEFORE IT FIXED IT. Collapsing two renderers into one put all seven variants
* behind a single unbounded interpolation site, so the consolidation raised the stake and then made
* the repair one wrap per arm.
*
* ⭐ Callers holding only a {@link SchemaDerivationFailure} need no change beyond the name. The
* narrow union is a subset of the wide one, so a derivation failure is assignable to this
* parameter.
*/
function schemaFailureMessage(f) {
	switch (f.kind) {
		case "app-slug-illegal": return `app slug '${safeRuleLabel(f.appSlug)}' does not yield a legal Postgres schema name.`;
		case "fixture-id-illegal": return `fixture id '${safeRuleLabel(f.fixtureId)}' does not yield a legal Postgres schema name.`;
		case "too-long": return `derived schema name '${safeRuleLabel(f.derived)}' is ${f.length} characters, over the Postgres limit of 63. Shorten the app slug or the fixture id.`;
		case "declared-illegal": return `declared schema name '${safeRuleLabel(f.declared)}' does not match ${String(SCHEMA_NAME_PATTERN)}. Remove it: the platform derives '<appSlug>_<fixtureId>' for you.`;
		case "declared-too-long": return `declared schema name '${safeRuleLabel(f.declared)}' is ${f.length} characters, over the Postgres limit of 63.`;
		case "declared-reserved": return `declared schema name '${safeRuleLabel(f.declared)}' is a platform-owned schema and cannot be owned by a fixture. The platform owns ${[...RESERVED_SCHEMAS].sort().join(", ")}.`;
		case "read-schema-reserved": return `readSchemas entry '${safeRuleLabel(f.entry)}' is a platform-owned schema and cannot be read by a fixture. The platform owns ${[...RESERVED_SCHEMAS].sort().join(", ")}.`;
		default: throw new Error(`unhandled schema failure variant: ${JSON.stringify(f)}`);
	}
}
//#endregion
export { PG_NAMEDATALEN, RESERVED_SCHEMAS, SCHEMA_NAME_PATTERN, deriveFixtureSchemaName, hasDeclaredSchema, resolveFixtureSchemaName, schemaFailureMessage };

//# sourceMappingURL=fixture-schema-derivation.js.map